import isNil from 'lodash/isNil';

import { RawAssetTypeCode } from 'types/models/asset-type';
import Sample from 'types/models/sample';
import SamplePointStatistic, { MergedSamplePointStatistic, SoilSummary } from 'types/models/samplePointsStatistic';
import { mergeSoilMoistureAndTempSamples } from 'utils/associated-sample-points/merge-soil-moisture-temperature';
import roundNumber from 'utils/round-number';
import { assertIsSamplePointStatisticWithAggregates } from 'utils/SamplePointStatistics/assert-is-with-aggregates';

const getSampleDimRwValue = (
  lastSample: Sample | undefined,
  dataType: RawAssetTypeCode.SOIL_MOISTURE | RawAssetTypeCode.SOIL_TEMP
) => lastSample?.multiDimValues?.sampleDim?.find((sampleDim) => sampleDim.dataType === dataType)?.rwValue;

const resolveMoistureAndTempSamples = (
  statistic: SamplePointStatistic
): {
  moistureSample: Sample | undefined;
  temperatureSample: Sample | undefined
} => {
  const hiddenStatistic = (statistic as MergedSamplePointStatistic)._hidden;
  if (hiddenStatistic) {
    return {
      moistureSample: statistic.lastSample,
      temperatureSample: hiddenStatistic.lastSample
    };
  }

  if (statistic.assetTypeId === RawAssetTypeCode.SOIL_MOISTURE) {
    return {
      moistureSample: statistic.lastSample,
      temperatureSample: undefined
    };
  } else if (statistic.assetTypeId === RawAssetTypeCode.SOIL_TEMP) {
    return {
      moistureSample: undefined,
      temperatureSample: statistic.lastSample
    };
  }

  return {
    moistureSample: statistic.lastSample,
    temperatureSample: undefined
  };
};

const getMergedSoilLastSample = (statistic: SamplePointStatistic) => {
  if (statistic.lastSample?.multiDimValues?.sampleDim) {
    return statistic.lastSample;
  }

  const { moistureSample, temperatureSample } = resolveMoistureAndTempSamples(statistic);
  if (!moistureSample && !temperatureSample) return undefined;

  return mergeSoilMoistureAndTempSamples(moistureSample, temperatureSample);
};

export const getSoilSummary = (statistic: SamplePointStatistic): SoilSummary | undefined => {
  try {
    assertIsSamplePointStatisticWithAggregates(statistic);
  } catch (error) {
    // Do nothing. Data is not as expected to compute the intended summary.
    return;
  }

  // `highestAverage` and `lowestMinimum` aggregate keys must be suffixed with `Moisture` or `Temp`
  // See `mapSoilAggregates` and `mergeSoilSamplePointStatistics`
  const thisFortnightValues = statistic.aggregates?.thisFortnight?.values;

  const maxMoistureThisFortnight = !isNil(thisFortnightValues.highestAverageMoisture)
    ? roundNumber(thisFortnightValues.highestAverageMoisture)
    : null;
  const minMoistureThisFortnight = !isNil(thisFortnightValues.lowestMinimumMoisture)
    ? roundNumber(thisFortnightValues.lowestMinimumMoisture)
    : null;
  const maxTempThisFortnight = !isNil(thisFortnightValues.highestAverageTemp)
    ? roundNumber(thisFortnightValues.highestAverageTemp)
    : null;
  const minTempThisFortnight = !isNil(thisFortnightValues.lowestMinimumTemp)
    ? roundNumber(thisFortnightValues.lowestMinimumTemp)
    : null;

  const mergedLastSample = getMergedSoilLastSample(statistic);
  const latestMoistureRwValue = getSampleDimRwValue(mergedLastSample, RawAssetTypeCode.SOIL_MOISTURE);
  const latestMoisture = !isNil(latestMoistureRwValue) ? roundNumber(latestMoistureRwValue) : null;
  const latestTempRwValue = getSampleDimRwValue(mergedLastSample, RawAssetTypeCode.SOIL_TEMP);
  const latestTemp = !isNil(latestTempRwValue) ? roundNumber(latestTempRwValue) : null;

  return {
    maxMoistureThisFortnight,
    minMoistureThisFortnight,
    maxTempThisFortnight,
    minTempThisFortnight,
    latestMoisture,
    latestTemp
  };
};
